/**
 * SummaryPage — Primary forecast overview with 13-week chart, receipt/disbursement
 * breakdown, liquidity status, and facility position.
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui';
import { useForecastSummary, useGenerateForecast, useForecastTaskStatus, useFacilityConfig, useActionSummary, prefetchAllCashflowData } from './api';
import { formatCurrency, formatPct, liquidityBg, computeKPIs, formatRetrievedAt, formatWeekLabel } from './utils';
import { cn } from '../../shared/lib/cn';
import type { WeeklyForecastRow } from './types';
import { useQueryClient } from '@tanstack/react-query';

function KPICard({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{label}</p>
        <p className={cn('text-2xl font-bold', accent || 'text-charcoal dark:text-gray-100')}>{value}</p>
        {sub && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function WeeklyTable({ weeks }: { weeks: WeeklyForecastRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="border-b border-gray-200 dark:border-dark-border">
            <th className="text-left py-2 px-3 font-medium text-gray-500 dark:text-gray-400 sticky left-0 bg-white dark:bg-dark-surface">Line Item</th>
            {weeks.map(w => (
              <th key={w.week_number} className="text-right py-2 px-2 font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                W{w.week_number}<br/>
                <span className="font-normal text-[10px]">{formatWeekLabel(w.week_start_date)}</span>
              </th>
            ))}
            <th className="text-right py-2 px-3 font-medium text-gray-500 dark:text-gray-400">Total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-dark-border/50">
          <SummaryRow label="Total Receipts" weeks={weeks} field="total_receipts" bold accent="text-emerald-700 dark:text-emerald-400" />
          <SummaryRow label="  AR (Current + 1-30 + 31-60)" weeks={weeks} field="total_contractual_receipts" />
          <SummaryRow label="  Forecasted (61-90 + 90+)" weeks={weeks} field="total_forecasted_receipts" />
          <SummaryRow label="Total Disbursements" weeks={weeks} field="total_disbursements" bold accent="text-red-700 dark:text-red-400" />
          <SummaryRow label="  Committed" weeks={weeks} field="total_committed_disbursements" />
          <SummaryRow label="  Discretionary" weeks={weeks} field="total_discretionary_disbursements" />
          <SummaryRow label="Net Cash Flow" weeks={weeks} field="net_cash_flow" bold signColor />
          <SummaryRow label="Beginning Cash" weeks={weeks} field="beginning_cash" />
          <SummaryRow label="Ending Cash" weeks={weeks} field="ending_cash" bold />
          <StatusRow label="Liquidity" weeks={weeks} />
        </tbody>
      </table>
    </div>
  );
}

function SummaryRow({
  label,
  weeks,
  field,
  bold,
  accent,
  signColor,
}: {
  label: string;
  weeks: WeeklyForecastRow[];
  field: keyof WeeklyForecastRow;
  bold?: boolean;
  accent?: string;
  signColor?: boolean;
}) {
  const total = weeks.reduce((s, w) => s + (w[field] as number), 0);
  const isIndented = label.startsWith('  ');

  return (
    <tr className={bold ? 'bg-gray-50 dark:bg-dark-surface-2/50' : ''}>
      <td className={cn(
        'py-1.5 px-3 sticky left-0 bg-inherit whitespace-nowrap',
        bold ? 'font-semibold' : 'font-normal',
        isIndented ? 'text-gray-500 dark:text-gray-400' : 'text-charcoal dark:text-gray-200',
      )}>
        {label}
      </td>
      {weeks.map(w => {
        const val = w[field] as number;
        return (
          <td
            key={w.week_number}
            className={cn(
              'text-right py-1.5 px-2 tabular-nums',
              bold ? 'font-semibold' : '',
              accent || (signColor ? (val >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400') : 'text-charcoal dark:text-gray-200'),
            )}
          >
            {formatCurrency(val, true)}
          </td>
        );
      })}
      <td className={cn(
        'text-right py-1.5 px-3 tabular-nums font-semibold',
        accent || (signColor ? (total >= 0 ? 'text-emerald-600' : 'text-red-600') : ''),
      )}>
        {formatCurrency(total, true)}
      </td>
    </tr>
  );
}

function StatusRow({ label, weeks }: { label: string; weeks: WeeklyForecastRow[] }) {
  return (
    <tr>
      <td className="py-1.5 px-3 font-semibold sticky left-0 bg-white dark:bg-dark-surface text-charcoal dark:text-gray-200">
        {label}
      </td>
      {weeks.map(w => (
        <td key={w.week_number} className="text-center py-1.5 px-2">
          <span className={cn('inline-block px-1.5 py-0.5 rounded text-[10px] font-medium', liquidityBg(w.liquidity_status))}>
            {w.liquidity_status}
          </span>
        </td>
      ))}
      <td />
    </tr>
  );
}

function ForecastGeneratingBanner({ taskId, onComplete, onError }: {
  taskId: string;
  onComplete: () => void;
  onError: (msg: string) => void;
}) {
  const { data: taskStatus } = useForecastTaskStatus(taskId);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (taskStatus?.status === 'SUCCESS') onComplete();
    if (taskStatus?.status === 'FAILURE') onError(taskStatus.error || 'Forecast generation failed');
  }, [taskStatus?.status]);

  const statusLabel =
    taskStatus?.status === 'STARTED' ? 'Crunching the numbers...' :
    taskStatus?.status === 'SUCCESS' ? 'Complete!' :
    taskStatus?.status === 'FAILURE' ? 'Failed' :
    'Queued — waiting for worker...';

  const isActive = !taskStatus || (taskStatus.status !== 'SUCCESS' && taskStatus.status !== 'FAILURE');

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          {isActive && (
            <div className="flex-shrink-0 mt-0.5">
              <div className="w-8 h-8 rounded-full border-2 border-eliza-red border-t-transparent animate-spin" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100 mb-1">
              Generating 13-Week Forecast
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              {statusLabel}
            </p>

            {isActive && (
              <div className="space-y-2">
                <div className="w-full bg-gray-100 dark:bg-dark-surface-2 rounded-full h-1.5 overflow-hidden">
                  <div className="h-full bg-eliza-red rounded-full animate-pulse" style={{ width: '60%' }} />
                </div>
                <p className="text-xs text-gray-400">
                  This typically takes 60–120 seconds. Elapsed: {elapsed}s
                </p>
              </div>
            )}

            <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-400">
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Loading source data
              </span>
              <span className="flex items-center gap-1.5">
                <span className={cn('w-1.5 h-1.5 rounded-full', elapsed > 3 ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600')} />
                Computing forecast
              </span>
              <span className="flex items-center gap-1.5">
                <span className={cn('w-1.5 h-1.5 rounded-full', elapsed > 10 ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600')} />
                Writing results
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SummaryPage() {
  const qc = useQueryClient();
  const { data: summary, isLoading, error } = useForecastSummary();
  const generateForecast = useGenerateForecast();
  const { data: facility } = useFacilityConfig();
  const { data: actionSummary } = useActionSummary();

  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [taskError, setTaskError] = useState<string | null>(null);
  const isGenerating = generateForecast.isPending || !!activeTaskId;

  const handleGenerate = useCallback(() => {
    setTaskError(null);
    generateForecast.mutate(undefined, {
      onSuccess: (data) => {
        setActiveTaskId(data.task_id);
      },
      onError: (err) => {
        setTaskError(err instanceof Error ? err.message : 'Failed to start forecast generation');
      },
    });
  }, [generateForecast]);

  const handleTaskComplete = useCallback(() => {
    setActiveTaskId(null);
    qc.invalidateQueries({ queryKey: ['cashflow'] });
    void prefetchAllCashflowData(qc, { forceRefresh: true });
  }, [qc]);

  const handleTaskError = useCallback((msg: string) => {
    setActiveTaskId(null);
    setTaskError(msg);
  }, []);

  const kpis = useMemo(() => (summary?.weeks ? computeKPIs(summary.weeks) : null), [summary]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-gray-400">Loading forecast data...</div>
      </div>
    );
  }

  if (activeTaskId) {
    return (
      <div className="space-y-6 mt-2">
        <ForecastGeneratingBanner
          taskId={activeTaskId}
          onComplete={handleTaskComplete}
          onError={handleTaskError}
        />
      </div>
    );
  }

  if (error || !summary?.weeks?.length) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-8 text-center">
            <h2 className="text-lg font-semibold text-charcoal dark:text-gray-100 mb-2">
              No Forecast Available
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Generate your first 13-week cash flow forecast to get started.
            </p>
            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className={cn(
                'px-5 py-2.5 rounded-lg text-sm font-medium text-white transition-all',
                'bg-eliza-red hover:bg-eliza-red/90 disabled:opacity-50',
              )}
            >
              {generateForecast.isPending ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Starting...
                </span>
              ) : 'Generate Forecast'}
            </button>
            {taskError && (
              <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400 text-sm">
                {taskError}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-2">
      {/* KPI Cards */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4">
          <KPICard
            label="Total Receipts"
            value={formatCurrency(kpis.total_receipts_13w, true)}
            sub="13-week total"
            accent="text-emerald-600 dark:text-emerald-400"
          />
          <KPICard
            label="Total Disbursements"
            value={formatCurrency(kpis.total_disbursements_13w, true)}
            sub="13-week total"
            accent="text-red-600 dark:text-red-400"
          />
          <KPICard
            label="Net Cash Flow"
            value={formatCurrency(kpis.net_cash_flow_13w, true)}
            accent={kpis.net_cash_flow_13w >= 0 ? 'text-emerald-600' : 'text-red-600'}
          />
          <KPICard
            label="Avg Weekly Burn"
            value={formatCurrency(kpis.average_weekly_burn, true)}
            sub={`${kpis.weeks_of_runway.toFixed(1)} weeks runway`}
          />
          <KPICard
            label="Open Actions"
            value={String(actionSummary?.total_open ?? 0)}
            sub={actionSummary?.total_overdue ? `${actionSummary.total_overdue} overdue` : undefined}
            accent={actionSummary?.total_overdue ? 'text-red-600' : undefined}
          />
          <KPICard
            label="Facility Utilization"
            value={formatPct(kpis.utilization_peak_pct)}
            sub={facility ? `${formatCurrency(facility.total_commitment, true)} total` : undefined}
          />
        </div>
      )}

      {/* Weekly Forecast Table */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-border flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">
                13-Week Forecast
              </h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {formatRetrievedAt(summary.retrieved_at)}
                {summary.data_status === 'stale' ? ' - stale fallback' : summary.is_cached ? ' - cached' : ''}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">v{summary.version} &middot; {summary.status}</span>
              <button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="px-3 py-1 text-xs font-medium rounded bg-gray-100 dark:bg-dark-surface-2 hover:bg-gray-200 dark:hover:bg-dark-border text-charcoal dark:text-gray-200 disabled:opacity-50"
              >
                {isGenerating ? (
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin" />
                    Generating...
                  </span>
                ) : 'Regenerate'}
              </button>
            </div>
          </div>
          {summary.data_warning && (
            <div className="px-4 py-2 border-b border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/20 text-xs text-amber-800 dark:text-amber-300">
              {summary.data_warning}
            </div>
          )}
          <WeeklyTable weeks={summary.weeks} />
        </CardContent>
      </Card>

      {/* Task error banner */}
      {taskError && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400 text-sm flex items-center justify-between">
          <span>{taskError}</span>
          <button onClick={() => setTaskError(null)} className="text-xs underline ml-4">Dismiss</button>
        </div>
      )}

      {/* Footnote for V1 estimate methodology */}
      <p className="text-[11px] text-gray-400 dark:text-gray-500 italic">
        * Collection timing uses bucket-average estimates. Customer-specific payment behavior data, when available, will improve accuracy.
      </p>
    </div>
  );
}
